import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { useArInvoicesList } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { Currency } from '@/components/common/Currency';
import { useDebouncedState } from '@react-hookz/web';
import { T, useGT } from 'gt-react';

interface InvoiceSearchComboboxProps {
  onSelect: (invoiceId: string) => void;
  onCancel: () => void;
}

export function InvoiceSearchCombobox({
  onSelect,
  onCancel,
}: InvoiceSearchComboboxProps) {
  const gt = useGT();
  const [debouncedSearch, setDebouncedSearch] = useDebouncedState('', 300);

  const { invoices, isLoading } = useArInvoicesList(
    debouncedSearch.length >= 2
      ? { search: debouncedSearch, limit: 10 }
      : { limit: 10 },
  );

  return (
    <Popover open onOpenChange={(open) => !open && onCancel()}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline">
          <T context="invoice search combobox trigger button">
            Search invoices...
          </T>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={gt('Type invoice number...', {
              context: 'invoice search combobox input placeholder',
            })}
            value={debouncedSearch}
            onValueChange={setDebouncedSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? gt('Searching...', {
                    context: 'invoice search combobox loading state',
                  })
                : gt('No invoices found', {
                    context: 'invoice search combobox empty state',
                  })}
            </CommandEmpty>
            <CommandGroup>
              {invoices.map((inv) => (
                <CommandItem
                  key={inv.id}
                  value={inv.id}
                  onSelect={() => onSelect(inv.id)}
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">
                      {inv.invoiceNumber ??
                        gt('No #', {
                          context:
                            'invoice search combobox item, invoice with no number',
                        })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {inv.customerName ??
                        gt('Unknown', {
                          context:
                            'invoice search combobox item, unknown customer name',
                        })}{' '}
                      &middot;{' '}
                      <T context="invoice search combobox item balance label">
                        Balance:
                      </T>{' '}
                      <Currency value={inv.balanceDue} fallback="--" />
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
